import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { BottomNav } from './BottomNav';
import { ActingAsBar } from './ActingAsBar';
import { TourBar } from '@/components/tour/TourBar';
import { useCustomer } from '@/hooks/useCustomer';
import { useIsSuperadmin } from '@/hooks/admin/useIsSuperadmin';
import { useDeskAccess } from '@/hooks/usePhoneDesk';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { PastDueModal } from '@/components/PastDueModal';
import { CylinderAuditDueBanner } from '@/components/CylinderAuditDueBanner';
import { BubblyWidget } from '@/components/bubbly/BubblyWidget';
import { BubblyProvider } from '@/components/bubbly/BubblyContext';

/**
 * AppShell — desktop chrome + mobile bottom-nav wrapper for every
 * authenticated route. The ordering location is resolved from the live
 * `useCustomer` context — never from a hardcoded fixture.
 *
 * "1:1 account" model: a normal account has exactly ONE location (its
 * primary) and there is no location-picker noise — we auto-select it and
 * hide any switcher. Only a MASTER account (a QBO customer that actually
 * has sub-customers) has multiple locations, and only then does a picker
 * surface (see LocationPicker, rendered by pages when locations.length > 1).
 *
 * We track the selection by location *id* (stable) and expose the matching
 * display label for chrome that wants to show "where am I ordering".
 */
export function AppShell() {
  const { data: ctx } = useCustomer();
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  // Once ctx resolves, auto-select the user's default location, falling back
  // to the first available real location. We only set it while it's still
  // empty (or no longer valid for this account) so a manual switch on a
  // master account sticks across re-renders.
  useEffect(() => {
    if (!ctx) return;
    const stillValid =
      selectedLocationId != null &&
      ctx.locations.some((l) => l.id === selectedLocationId);
    if (stillValid) return;
    const def = ctx.locations.find((l) => l.id === ctx.user.defaultLocationId);
    const next = def?.id ?? ctx.locations[0]?.id ?? null;
    setSelectedLocationId(next);
  }, [ctx, selectedLocationId]);

  // Resolve the label for the active location id (display only).
  const selectedLocation = useMemo(() => {
    if (!ctx) return '';
    const match = ctx.locations.find((l) => l.id === selectedLocationId);
    return match?.displayName ?? ctx.locations[0]?.displayName ?? '';
  }, [ctx, selectedLocationId]);

  // Single-location (1:1) accounts have no picker; >1 (master) accounts do.
  const isSingleLocation = (ctx?.locations.length ?? 0) <= 1;

  // Bubbly is a customer-facing assistant — show it on the customer app only,
  // never inside the superadmin /admin area (which shares this shell).
  const { pathname } = useLocation();
  const showBubbly = !pathname.startsWith('/admin');

  // First-run onboarding diversion: a brand-new CUSTOMER user (not a
  // superadmin, not an admin page) who hasn't finished onboarding is sent to
  // the /welcome wizard. Superadmins — including when acting as a customer —
  // are never diverted. Wait until superadmin + onboarding status resolve so a
  // superadmin isn't briefly bounced. (docs/ACTING-CONTEXT sibling: onboarding)
  const { isSuperadmin, loading: saLoading } = useIsSuperadmin();
  const { data: onboarding } = useOnboardingStatus();
  const needsOnboarding =
    !saLoading &&
    !isSuperadmin &&
    !!ctx?.customer &&
    onboarding?.completed === false &&
    !pathname.startsWith('/admin');

  // A phone-desk login (the live receptionists) has no customer account and
  // no admin access: every customer and admin page sends them to /desk, which
  // is also where they land after signing in. Superadmins are never diverted.
  const { access: desk } = useDeskAccess();
  if (desk?.isPhoneDesk && !desk.isSuperadmin) {
    return <Navigate to="/desk" replace />;
  }

  if (needsOnboarding) {
    return <Navigate to="/welcome" replace />;
  }

  return (
    <BubblyProvider>
    <div data-app-shell className="relative min-h-screen bg-brix-surface flex flex-col">
      <div data-app-chrome aria-hidden className="pointer-events-none absolute inset-0 navy-glow-top" />

      <div className="relative flex flex-col flex-1">
        <Header />

        {/* Loud "acting as <customer>" reminder — customer pages only, never
            inside /admin (which ignores the acting context entirely). */}
        {showBubbly && <ActingAsBar />}
        {/* Persistent "Want a product tour?" bar (customer pages only). */}
        {showBubbly && <TourBar />}

        <div
          className="flex flex-1 overflow-hidden"
          style={{ height: 'calc(100vh - 64px)' }}
        >
          <div data-app-chrome className="hidden md:flex">
            <Sidebar />
          </div>

          <main data-tour="page-main" className="flex-1 min-w-0 flex flex-col overflow-hidden">
            {/* Audit-required flag — stays until the driver files the count. */}
            <CylinderAuditDueBanner />
            <Outlet
              context={{
                selectedLocation,
                selectedLocationId,
                setSelectedLocationId,
                isSingleLocation,
              }}
            />
          </main>
        </div>

        <BottomNav />
      </div>

      {/* True blocking past-due overlay — replaces the inline /home banner.
          Re-appears on /order + /cart even if the user temporarily dismisses
          it elsewhere. */}
      <PastDueModal />

      {/* Floating Bubbly assistant — customer pages only, not /admin. Login &
          Apply live outside this shell, so they're already excluded. */}
      {showBubbly && <BubblyWidget />}
    </div>
    </BubblyProvider>
  );
}
